import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom"; 
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import busStations from "../../../Data/BusStations.json";
import {
  RiCalendarLine,
  RiBusLine,
  RiMapPinLine,
  RiMapPinFill,
  RiSearchLine,
  RiSwapLine,
  RiArrowDownSLine,
  RiStarLine,
  RiTimeLine
} from "react-icons/ri";
import styles from "../SearchBusPanel/SearchBusPanel1.module.css";

// Custom date input with minimal design
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div onClick={onClick} ref={ref} className={styles.dateInputContainer}>
    <RiCalendarLine className={styles.calendarIcon} />
    <span className={`${styles.dateText} ${value ? styles.dateSelected : ''}`}>
      {value || 'Select date'}
    </span>
  </div>
));

const SearchBusPanel1 = ({ onSearch, mode, onNavigate, templateId }) => {
   const navigate = useNavigate();
  // Set default values to Hyderabad and Bangalore
  const [fromStation, setFromStation] = useState("HYDERABAD");
  const [toStation, setToStation] = useState("BANGALORE");
  const [fromStationId, setFromStationId] = useState("");
  const [toStationId, setToStationId] = useState("");
  const [travelDate, setTravelDate] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [fromStationResults, setFromStationResults] = useState([]);
  const [toStationResults, setToStationResults] = useState([]);
  
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const handleSwapStations = () => {
    setFromStation(toStation);
    setToStation(fromStation);
    setFromStationId(toStationId);
    setToStationId(fromStationId);
  };

  const handleFromStationSearch = (e) => {
    const value = e.target.value;
    setFromStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 8);
      setFromStationResults(filteredStations);
    } else {
      setFromStationResults([]);
    }
  };

  const handleToStationSearch = (e) => {
    const value = e.target.value;
    setToStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 8);
      setToStationResults(filteredStations);
    } else {
      setToStationResults([]);
    }
  };

  const handleFromStationSelect = (station) => {
    setFromStation(station.stationName);
    setFromStationId(station.stationId);
    setFromStationResults([]);
    setActiveField(null);
  };

  const handleToStationSelect = (station) => {
    setToStation(station.stationName);
    setToStationId(station.stationId);
    setToStationResults([]);
    setActiveField(null);
  };

  const validate = () => {
    let temp = {};
    temp.fromStation = fromStation !== "";
    temp.toStation = toStation !== "";
    temp.travelDate = travelDate !== null;
    temp.sameStation = fromStation !== toStation;
    
    setErrors(temp);
    return Object.values(temp).every(x => x === true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const searchQuery = {
        fromStationCode: fromStationId,
        fromStation: fromStation,
        toStationCode: toStationId,
        toStation: toStation,
        travelDate: moment(travelDate).format("YYYY-MM-DD"),
      };

      if (mode === "builder") {
        onNavigate({ name: "bus-results", params: { searchQuery } });
      } else if (mode === "preview") {
        navigate(`/template-preview/${templateId}/bus-results`, { state: { searchQuery } });
      } else {
        onSearch(searchQuery); // For live site
      }
    }
  };


  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.stationDropdown}`)) {
        setFromStationResults([]);
        setToStationResults([]);
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.container}>
      {/* Main search bar with labels */}
      <div className={styles.searchCard}>
        
        {/* Labels row */}
        <div className={styles.labelsRow}>
          <div className={styles.fromLabel}>
            <span className={styles.labelText}>FROM</span>
          </div>
          <div className={styles.swapSpacer}></div>
          <div className={styles.toLabel}>
            <span className={styles.labelText}>TO</span>
          </div>
          <div className={styles.dateLabel}>
            <span className={styles.labelText}>DEPARTURE DATE</span>
          </div>
          <div className={styles.searchSpacer}></div>
        </div>

        {/* Search inputs row */}
        <div className={styles.inputsRow}>
          
          {/* From Station */}
          <div className={styles.fromContainer}>
            <div className={`${styles.inputWrapper} ${activeField === 'from' ? styles.inputActive : ''} ${errors.fromStation === false ? styles.inputError : ''}`}>
              <RiMapPinLine className={styles.fromIcon} />
              <input
                ref={fromInputRef}
                type="text"
                placeholder="Leaving from"
                value={fromStation}
                onChange={handleFromStationSearch}
                onFocus={() => {
                  setActiveField('from');
                  const filteredStations = busStations.stationList.slice(0, 8);
                  setFromStationResults(filteredStations);
                }}
                className={styles.input}
              />
            </div>
            
            {/* From station results */}
            {fromStationResults.length > 0 && activeField === 'from' && (
              <ul className={`${styles.resultsList} ${styles.stationDropdown}`}>
                {fromStationResults.map((station) => (
                  <li
                    key={station.stationId}
                    onClick={() => handleFromStationSelect(station)}
                    className={styles.resultItem}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.paddingLeft = '24px';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.paddingLeft = '16px';
                    }}
                  >
                    <RiMapPinLine className={styles.resultIcon} />
                    <span className={styles.resultText}>{station.stationName}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Swap button */}
          <div
            className={styles.swapButton}
            onClick={handleSwapStations}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3b82f6';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'rotate(180deg) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
            }}
          >
            <RiSwapLine className={styles.swapIcon} />
          </div>

          {/* To Station */}
          <div className={styles.toContainer}>
            <div className={`${styles.inputWrapper} ${activeField === 'to' ? styles.inputActive : ''} ${errors.toStation === false ? styles.inputError : ''}`}>
              <RiMapPinFill className={styles.toIcon} />
              <input
                ref={toInputRef}
                type="text"
                placeholder="Going to"
                value={toStation}
                onChange={handleToStationSearch}
                onFocus={() => {
                  setActiveField('to');
                  const filteredStations = busStations.stationList.slice(0, 8);
                  setToStationResults(filteredStations);
                }}
                className={styles.input}
              />
            </div>
            
            {/* To station results */}
            {toStationResults.length > 0 && activeField === 'to' && (
              <ul className={`${styles.resultsList} ${styles.stationDropdown}`}>
                {toStationResults.map((station) => (
                  <li
                    key={station.stationId}
                    onClick={() => handleToStationSelect(station)}
                    className={styles.resultItem}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.paddingLeft = '24px';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.paddingLeft = '16px';
                    }}
                  >
                    <RiMapPinFill className={styles.resultIcon} />
                    <span className={styles.resultText}>{station.stationName}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Date Picker */}
          <div className={styles.dateContainer}>
            <DatePicker
              selected={travelDate}
              onChange={setTravelDate}
              customInput={<CustomDateInput />}
              dateFormat="dd-MMM-yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              minDate={new Date()}
              yearDropdownItemNumber={50}
              scrollableYearDropdown
              placeholderText="Select date"
            />
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            className={styles.searchButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(59,130,246,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 10px rgba(59,130,246,0.3)';
            }}
          >
            <RiSearchLine className={styles.searchIcon} />
            SEARCH
          </button>
        </div>
      </div>

      {/* Error messages */}
      <div className={styles.errorContainer}>
        {errors.fromStation === false && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            Please select departure station
          </div>
        )}
        {errors.toStation === false && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            Please select destination station
          </div>
        )}
        {errors.travelDate === false && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            Please select journey date
          </div>
        )}
        {errors.sameStation === false && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            From and To stations cannot be same
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBusPanel1;